import * as StorageUtils from "@/utils/StorageUtils"
import Endpoint from "@/enums/Endpoint"
import { ApiError } from "@/utils/ApiError"
import { reportApiError } from "@/utils/ErrorTelemetry"
import { getTokenExpiryMs } from "@/utils/tokenUtils"
import { getTaxiSystemCode } from "@/utils/taxiSystem"
import type { AuthTokens } from "@/types/authTokens"
import type { RefreshTokenBody } from "@/types/request/refreshTokenBody"

const ITEST_BASE_URL = "https://itf-bookingterminalapi-itest.itf-as.com:40401/api/v1/"
const ATEST_BASE_URL = "https://itf-bookingterminalapi-atest.itf-as.com/api/v1/"

// Refresh proactively when less than this remains on the access token
const REFRESH_MARGIN_MS = 4 * 60 * 1000

export function getAPIBaseURL(): string {
  if (import.meta.env.DEV) {
    return "/api/v1/"
  }

  const origin = window.location.origin.toLowerCase()
  if (origin.includes("-itest")) return ITEST_BASE_URL
  if (origin.includes("-atest")) return ATEST_BASE_URL

  return `https://${getTaxiSystemCode()}-bookingterminalapi.itf-as.com/api/v1/`
}

type Method = "GET" | "POST" | "PUT" | "DELETE"

const ANONYMOUS_ENDPOINTS: Endpoint[] = [Endpoint.LOGIN, Endpoint.REFRESH_TOKEN]

let refreshInFlight: Promise<boolean> | null = null

async function forceLogout(): Promise<void> {
  // Import auth store dynamically to avoid a circular dependency
  const { useAuthStore } = await import("@/stores/auth")
  await useAuthStore().logout({ expired: true })
}

// Exchanges the refresh token for a new pair. Concurrent callers share one request.
export function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const accessToken = StorageUtils.getAuthToken()
    const refreshToken = StorageUtils.getRefreshToken()
    if (!accessToken || !refreshToken) return false

    const body: RefreshTokenBody = {
      accessToken,
      refreshToken,
      clientId: StorageUtils.getOrCreateClientId()
    }

    try {
      const tokens = await rawRequest<AuthTokens>(Endpoint.REFRESH_TOKEN, "POST", body, null)
      if (tokens?.accessToken && tokens.refreshToken) {
        StorageUtils.saveAuthTokens(tokens.accessToken, tokens.refreshToken)
        return true
      }
      return false
    } catch (error) {
      console.warn("Token refresh failed:", error)
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

function tokenNeedsRefresh(token: string): boolean {
  const expiryMs = getTokenExpiryMs(token)
  if (expiryMs === null) return false
  return expiryMs - Date.now() < REFRESH_MARGIN_MS
}

interface RequestOptions {
  // Lets the request finish when the page goes away mid-flight — an order sent as the user leaves the app
  keepalive?: boolean
}

async function rawRequest<ResponseData>(
  endPoint: Endpoint,
  method: Method,
  requestBody: {} | undefined,
  authToken: string | null,
  options: RequestOptions = {}
): Promise<ResponseData> {
  const headers: Record<string, string> = {
    Accept: "application/json, text/plain, */*",
    "Content-Type": "application/json"
  }
  if (authToken) headers.Authorization = authToken

  const response = await fetch(`${getAPIBaseURL()}${endPoint}`, {
    method,
    headers,
    body: requestBody === undefined ? undefined : JSON.stringify(requestBody),
    keepalive: options.keepalive
  })

  if (!response.ok) {
    let errorBody: string | undefined
    try {
      errorBody = await response.text()
    } catch {}

    console.error(`API Error: ${response.status} ${response.statusText}`, errorBody)
    // API errors are reported to Sentry once, here — catch sites skip ApiError to avoid duplicates
    reportApiError(endPoint, response.status, errorBody)

    throw new ApiError(response.status, response.statusText, errorBody)
  }

  return (await response.json()) as ResponseData
}

// The access token, refreshed first when it is about to run out
export async function freshAuthToken(): Promise<string | null> {
  const authToken = StorageUtils.getAuthToken()
  if (authToken && tokenNeedsRefresh(authToken) && (await refreshTokens())) return StorageUtils.getAuthToken()
  return authToken
}

export async function performRequest<ResponseData>(endPoint: Endpoint, method: Method, requestBody?: {}, options: RequestOptions = {}): Promise<ResponseData> {
  if (ANONYMOUS_ENDPOINTS.includes(endPoint)) {
    return rawRequest<ResponseData>(endPoint, method, requestBody, null)
  }

  const authToken = await freshAuthToken()

  try {
    return await rawRequest<ResponseData>(endPoint, method, requestBody, authToken, options)
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // The access token may have been revoked or expired between checks — one retry after refresh
      if (await refreshTokens()) {
        return rawRequest<ResponseData>(endPoint, method, requestBody, StorageUtils.getAuthToken(), options)
      }
      console.warn("Session expired - logging out")
      await forceLogout()
    }
    throw error
  }
}
